/**
 * One messaging state across the windows of a browser (docs/modules/messaging.md "Three
 * presentation modes, one state"): a `BroadcastChannel('oex-messages')` carries read markers,
 * drafts and new messages between the dock, the full page and pop-outs. Drafts also persist in
 * localStorage. Realtime itself comes from the shell's single SSE stream; pop-outs opened from
 * the app render into their window from this one (see `popout.tsx`) and therefore share that
 * stream and this sync object instead of opening their own.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

export const CHANNEL_NAME = 'oex-messages';
const DRAFT_KEY = (threadId: string) => `oex.messages.draft.${threadId}`;

export type SyncEvent =
  | { type: 'message'; threadId: string; messageId: string }
  | { type: 'read'; threadId: string; messageId: string }
  | { type: 'thread'; threadId: string }
  | { type: 'draft'; threadId: string; body: string };

type Envelope = SyncEvent & { origin: string };
type ChannelLike = Pick<BroadcastChannel, 'postMessage' | 'close'> & {
  onmessage: ((e: MessageEvent) => void) | null;
};

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export class MessagingSync {
  readonly origin = Math.random().toString(36).slice(2);
  private readonly channel: ChannelLike | null;
  private readonly listeners = new Set<(e: SyncEvent, remote: boolean) => void>();
  private readonly draftListeners = new Set<() => void>();
  private readonly drafts = new Map<string, string>();

  constructor(factory: ((name: string) => ChannelLike) | null = defaultFactory()) {
    this.channel = factory ? factory(CHANNEL_NAME) : null;
    if (this.channel) {
      this.channel.onmessage = (e: MessageEvent) => {
        const data = e.data as Envelope;
        if (!data || data.origin === this.origin) return;
        const { origin: _o, ...event } = data;
        this.apply(event as SyncEvent, true);
      };
    }
  }

  private apply(event: SyncEvent, remote: boolean) {
    if (event.type === 'draft') {
      this.drafts.set(event.threadId, event.body);
      for (const fn of this.draftListeners) fn();
    }
    for (const fn of this.listeners) fn(event, remote);
  }

  /** Deliver to this window's listeners and to every other window. */
  post(event: SyncEvent): void {
    this.apply(event, false);
    this.channel?.postMessage({ ...event, origin: this.origin } satisfies Envelope);
  }

  subscribe(fn: (e: SyncEvent, remote: boolean) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  draft(threadId: string): string {
    if (!this.drafts.has(threadId)) this.drafts.set(threadId, storage()?.getItem(DRAFT_KEY(threadId)) ?? '');
    return this.drafts.get(threadId) ?? '';
  }

  setDraft(threadId: string, body: string): void {
    const s = storage();
    try {
      if (body) s?.setItem(DRAFT_KEY(threadId), body);
      else s?.removeItem(DRAFT_KEY(threadId));
    } catch {
      // storage full or blocked: the draft still syncs in memory
    }
    this.post({ type: 'draft', threadId, body });
  }

  subscribeDrafts(fn: () => void): () => void {
    this.draftListeners.add(fn);
    return () => this.draftListeners.delete(fn);
  }

  close(): void {
    this.channel?.close();
    this.listeners.clear();
  }
}

function defaultFactory(): ((name: string) => ChannelLike) | null {
  return typeof BroadcastChannel === 'undefined' ? null : (name) => new BroadcastChannel(name) as ChannelLike;
}

let singleton: MessagingSync | null = null;
export function defaultSync(): MessagingSync {
  singleton ??= new MessagingSync();
  return singleton;
}

const SyncContext = createContext<MessagingSync | null>(null);

/** Tests (two "windows" in one jsdom) give each tree its own sync object. */
export function MessagingSyncProvider({ sync, children }: { sync: MessagingSync; children: ReactNode }) {
  return createElement(SyncContext.Provider, { value: sync }, children);
}

export function useMessagingSync(): MessagingSync {
  return useContext(SyncContext) ?? defaultSync();
}

/** Other windows' sends and reads refresh this window's messaging queries. */
export function useSyncInvalidation(): void {
  const sync = useMessagingSync();
  const qc = useQueryClient();
  useEffect(
    () =>
      sync.subscribe((e, remote) => {
        if (remote && e.type !== 'draft') void qc.invalidateQueries({ queryKey: ['messaging'] });
      }),
    [sync, qc],
  );
}

/** A thread's draft, shared by the dock, the page and pop-outs. */
export function useDraft(threadId: string): [string, (body: string) => void] {
  const sync = useMessagingSync();
  const value = useSyncExternalStore(
    (fn) => sync.subscribeDrafts(fn),
    () => sync.draft(threadId),
    () => '',
  );
  return [value, (body: string) => sync.setDraft(threadId, body)];
}
